import React, { useState, useEffect } from 'react'
import { Portal } from 'react-portal'

import { MobileNavigationHeader } from '../Header/Header.styles'
import Modal from 'components/Modal'
import ModalTransition from 'containers/ModalManager/ModalTransition'
import { Truncate } from '@sketch/components'
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  CancelButton,
  Wrapper,
  BackButton,
  BackIcon,
  Trigger,
} from './MobileGroupsDropdown.styles'

interface MobileDropdownProps {
  type: string
  selected: string
  level?: number
  handleBack: () => void
  handleCloseMenu?: () => void
}

/**
 * Shows the component groups sidebar for mobile views, it's similar to
 * <SelectDropdown /> from projects, but requires some exceptions
 * (rendering a collapsible tree instead of a regular list for example), so I
 * decided to duplicate and tweak instead of creating a new abstraction.
 */
export const MobileGroupsDropdown: React.FC<MobileDropdownProps> = ({
  type,
  selected,
  children,
  level = 0,
  handleBack,
  handleCloseMenu,
}) => {
  const [hasOverflow, setHasOverflow] = useState(false)
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const closeMenu = () => {
    setMobileMenuVisible(false)
    handleCloseMenu?.()
  }
  const openMenu = () => setMobileMenuVisible(true)

  // Close the menu when an item is selected
  useEffect(() => {
    if (mobileMenuVisible) {
      closeMenu()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  const header =
    level === 0 ? (
      type
    ) : (
      <BackButton onClick={handleBack}>
        <BackIcon />
        {type}
      </BackButton>
    )

  return (
    <Wrapper>
      <Trigger
        type="button"
        aria-haspopup
        aria-expanded={mobileMenuVisible}
        onClick={openMenu}
      >
        <Truncate>{selected}</Truncate>
      </Trigger>
      <Portal>
        <ModalTransition show={mobileMenuVisible}>
          <Modal onCancel={closeMenu} onUpdateScroll={setHasOverflow}>
            <ModalHeader as={MobileNavigationHeader}>{header}</ModalHeader>
            <ModalBody>{children}</ModalBody>
            <ModalFooter hasOverflow={hasOverflow}>
              <CancelButton variant="tertiary" small onClick={closeMenu} fill>
                Cancel
              </CancelButton>
            </ModalFooter>
          </Modal>
        </ModalTransition>
      </Portal>
    </Wrapper>
  )
}
